import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { cell, humanise, isNumeric } from '../format';
import { animation, tooltipChrome } from '../options';

/**
 * Hierarchical part-to-whole drawn as concentric rings — the inner ring is the
 * grouping, the outer ring its members, each arc's sweep its share.
 *
 * Expects a group column, a label column and a numeric column
 * (`[group, name, value]`); with only one dimension it degrades to a single
 * ring (a pie by another name). Colour is by top-level group, so a segment's
 * ring shares its parent's hue.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

interface Node {
  name: string;
  value?: number;
  children?: Node[];
  itemStyle?: { color: string };
}

const sunburstView = (data: ChartData, theme: ReturnType<typeof useTheme>) => {
  if (!data.columns.length || !data.rows.length) return null;

  const numericAt = (i: number) => data.rows.slice(0, 30).some((r) => isNumeric(r[i]));
  const measureIdx = data.columns.findIndex((_, i) => numericAt(i));
  const dims = data.columns.map((_, i) => i).filter((i) => !numericAt(i));
  if (measureIdx < 0 || dims.length < 1) return null;

  const text = (v: unknown) => String(v ?? '');
  const groupIdx = dims.length > 1 ? dims[0] : -1;
  const labelIdx = dims.length > 1 ? dims[1] : dims[0];
  const measureName = humanise(data.columns[measureIdx]);

  let nodes: Node[];
  if (groupIdx >= 0) {
    const groups = new Map<string, Node>();
    for (const r of data.rows) {
      if (!isNumeric(r[measureIdx])) continue;
      const g = text(r[groupIdx]) || '—';
      let parent = groups.get(g);
      if (!parent) {
        parent = { name: g, children: [] };
        groups.set(g, parent);
      }
      parent.children!.push({ name: text(r[labelIdx]), value: r[measureIdx] as number });
    }
    nodes = [...groups.values()].map((g, i) => ({ ...g, itemStyle: { color: seriesColor(theme, i) } }));
  } else {
    nodes = data.rows
      .filter((r) => isNumeric(r[measureIdx]))
      .map((r, i) => ({
        name: text(r[labelIdx]),
        value: r[measureIdx] as number,
        itemStyle: { color: seriesColor(theme, i % 8) },
      }));
  }

  if (!nodes.length) return null;

  const option: EChartsOption = {
    ...animation,
    tooltip: {
      trigger: 'item',
      ...tooltipChrome(theme),
      formatter: (params: unknown) => {
        const p = params as { name: string; value?: number };
        const root = document.createElement('div');
        root.className = 'chart-tip';
        const head = document.createElement('div');
        head.className = 'chart-tip__title';
        head.textContent = p.name;
        root.append(head);
        if (p.value != null) {
          const row = document.createElement('div');
          row.className = 'chart-tip__row';
          const n = document.createElement('span');
          n.className = 'chart-tip__name';
          n.textContent = measureName;
          const v = document.createElement('strong');
          v.className = 'chart-tip__value';
          v.textContent = cell(p.value, 'exact');
          row.append(n, v);
          root.append(row);
        }
        return root;
      },
    },
    series: [
      {
        type: 'sunburst' as const,
        data: nodes,
        radius: ['16%', '92%'],
        center: ['50%', '52%'],
        nodeClick: false,
        itemStyle: { borderColor: theme.surface, borderWidth: 2 },
        label: {
          color: '#fff',
          fontSize: 11,
          fontFamily: theme.font,
          overflow: 'truncate' as const,
          minAngle: 8,
        },
        levels: [
          {},
          { r0: '16%', r: '54%', label: { rotate: 'tangential' as const } },
          { r0: '54%', r: '92%', label: { align: 'right' as const } },
        ],
      },
    ],
  };

  return { option };
};

export const Sunburst = ({ data }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => sunburstView(data, theme), [data, theme]);
  if (!view) return <Empty message="Needs a label and a numeric column" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Sunburst chart" />
    </Plot>
  );
};
